/**
 * Google Analytics 4 Utility Functions
 * 
 * Provides helper functions for tracking page views and events
 */

// Check if gtag is available
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Track a page view
 * @param {string} path - The page path (e.g., '/tools')
 * @param {string} title - The page title
 */
export const trackPageView = (path, title) => {
  if (!isGtagAvailable()) {
    console.warn('Google Analytics not loaded');
    return;
  }

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href
  });

  console.log('GA4 Page View:', path, title);
};

/**
 * Track a custom event
 * @param {string} eventName - The event name (e.g., 'button_click', 'tool_visit')
 * @param {object} eventParams - Additional event parameters
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (!isGtagAvailable()) {
    console.warn('Google Analytics not loaded');
    return;
  }

  window.gtag('event', eventName, eventParams);
  console.log('GA4 Event:', eventName, eventParams);
};

/**
 * Track user signup
 * @param {string} method - Signup method (e.g., 'email', 'google')
 */
export const trackSignup = (method) => {
  trackEvent('sign_up', {
    method: method
  });
};

/**
 * Track user login
 * @param {string} method - Login method (e.g., 'email', 'google')
 */
export const trackLogin = (method) => {
  trackEvent('login', {
    method: method
  });
};

/**
 * Track external link click (e.g., visiting a tool)
 * @param {string} url - The external URL
 * @param {string} linkText - The link text or tool name
 */
export const trackOutboundLink = (url, linkText) => {
  trackEvent('click', {
    event_category: 'outbound',
    event_label: linkText,
    value: url
  });
};

/**
 * Track search
 * @param {string} searchTerm - The search query
 */
export const trackSearch = (searchTerm) => {
  trackEvent('search', {
    search_term: searchTerm
  });
};

/**
 * Track tool interaction
 * @param {string} toolName - The tool name
 * @param {string} action - The action (e.g., 'view', 'click', 'visit')
 */
export const trackToolInteraction = (toolName, action) => {
  trackEvent('tool_interaction', {
    tool_name: toolName,
    action: action
  });
};

/**
 * Set user properties
 * @param {object} properties - User properties to set
 */
export const setUserProperties = (properties) => {
  if (!isGtagAvailable()) {
    console.warn('Google Analytics not loaded');
    return;
  }

  window.gtag('set', 'user_properties', properties);
};

/**
 * Track timing/performance
 * @param {string} name - Timing name
 * @param {number} value - Time in milliseconds
 * @param {string} category - Optional category
 */
export const trackTiming = (name, value, category = 'Performance') => {
  trackEvent('timing_complete', {
    name: name,
    value: value,
    event_category: category
  });
};

export default {
  trackPageView,
  trackEvent,
  trackSignup,
  trackLogin,
  trackOutboundLink,
  trackSearch,
  trackToolInteraction,
  setUserProperties,
  trackTiming
};
